import { GetStaticProps, GetStaticPaths } from 'next';
import { getPostData, getAllPostIds, Post } from '@/lib/posts';
import { motion } from 'framer-motion';
import React from 'react';
import BlogContentRenderer from '@/components/BlogContentRenderer';
import DefaultLayout from '@/components/DefaultLayout';

// Block interfaces
interface HeadingBlock {
  type: 'heading';
  level: 1 | 2 | 3 | 4 | 5 | 6;
  text: string;
}
interface ParagraphBlock {
  type: 'paragraph';
  text: string;
}
interface ListBlock {
  type: 'list';
  ordered: boolean;
  items: string[];
}
interface ImageBlock {
  type: 'image';
  src: string;
  alt?: string;
  width?: number;
  height?: number;
}
interface VideoBlock {
  type: 'video';
  src: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  controls?: boolean;
}
interface QuoteBlock {
  type: 'quote';
  text: string;
  author?: string;
}
interface CodeBlock {
  type: 'code';
  code: string;
  language: string;
}
interface DividerBlock {
  type: 'divider';
}
type ContentBlock = HeadingBlock | ParagraphBlock | ListBlock | ImageBlock | VideoBlock | QuoteBlock | CodeBlock | DividerBlock;

interface LegacyContentBlock {
  type: 'text' | 'code' | 'quote' | 'media';
  content?: string;
  language?: string;
  author?: string;
  media?: {
    type: 'image' | 'video';
    src: string;
    alt?: string;
    width?: number;
    height?: number;
    autoPlay?: boolean;
    muted?: boolean;
    loop?: boolean;
    playsInline?: boolean;
    controls?: boolean;
  };
}

interface PostPageProps {
  postData: Post & { content: (ContentBlock | LegacyContentBlock)[] };
}

function isLegacyTextBlock(block: ContentBlock | LegacyContentBlock): block is LegacyContentBlock {
  return block.type === 'text';
}

function isLegacyCodeBlock(block: ContentBlock | LegacyContentBlock): block is LegacyContentBlock {
  return block.type === 'code';
}

function isLegacyQuoteBlock(block: ContentBlock | LegacyContentBlock): block is LegacyContentBlock {
  return block.type === 'quote';
}

function isLegacyMediaBlock(block: ContentBlock | LegacyContentBlock): block is LegacyContentBlock {
  return block.type === 'media';
}

function normalizeContent(content: (ContentBlock | LegacyContentBlock)[]): ContentBlock[] {
  return content.map((block) => {
    // Legacy text block
    if (isLegacyTextBlock(block)) {
      return { type: 'paragraph', text: block.content || '' };
    }
    // Legacy code block
    if (isLegacyCodeBlock(block) && block.content) {
      return { type: 'code', code: block.content, language: block.language || 'typescript' };
    }
    // Legacy quote block
    if (isLegacyQuoteBlock(block) && block.content) {
      return { type: 'quote', text: block.content, author: block.author };
    }
    // Legacy media block (image)
    if (isLegacyMediaBlock(block) && block.media?.type === 'image') {
      return {
        type: 'image',
        src: block.media.src,
        alt: block.media.alt,
        width: block.media.width,
        height: block.media.height,
      };
    }
    // Legacy media block (video)
    if (isLegacyMediaBlock(block) && block.media?.type === 'video') {
      return {
        type: 'video',
        src: block.media.src,
        autoPlay: block.media.autoPlay,
        muted: block.media.muted,
        loop: block.media.loop,
        playsInline: block.media.playsInline,
        controls: block.media.controls,
      };
    }
    // Already new format
    return block as ContentBlock;
  });
}

export default function PostPage({ postData }: PostPageProps) {
  const normalizedContent = normalizeContent(postData.content);
  return (
    <DefaultLayout>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 pb-12">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-blue-500 bg-clip-text text-transparent">
            {postData.title}
          </h1>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {new Date(postData.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
          {postData.tags && (
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {postData.tags.map(tag => (
                <span
                  key={tag}
                  className="px-4 py-1 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full text-sm font-medium text-purple-700 dark:text-purple-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </motion.header>
        <div>
          <BlogContentRenderer blocks={normalizedContent} />
        </div>
      </article>
    </DefaultLayout>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = getAllPostIds();
  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const postData = getPostData(params?.id as string);
  return {
    props: {
      postData,
    },
  };
}; 